<template>
  <v-container class="camera-container pa-4">
    <v-card elevation="4">
      <v-card-title class="bg-primary text-white d-flex align-center">
        <v-icon start icon="mdi-camera" class="mr-2"></v-icon>
        Camera Capture
        <v-spacer></v-spacer>
        <v-btn
          v-if="isStreaming && !capturedImage"
          variant="text"
          icon="mdi-camera-flip"
          @click="switchCamera"
          class="text-white"
        ></v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- Camera Preview Area -->
        <div class="camera-preview-wrapper position-relative">
          <video
            ref="videoElement"
            autoplay
            playsinline
            class="camera-preview"
            :class="{ hidden: !isStreaming || capturedImage }"
          ></video>

          <!-- Captured Image Preview -->
          <v-img
            v-if="capturedImage"
            :src="capturedImage"
            cover
            class="captured-preview"
          ></v-img>

          <!-- Loading Overlay -->
          <div
            v-if="isLoading"
            class="d-flex justify-center align-center loading-overlay"
          >
            <v-progress-circular
              indeterminate
              size="64"
              color="primary"
            ></v-progress-circular>
          </div>

          <!-- Permission Denied -->
          <v-alert v-if="permissionDenied" type="error" variant="tonal" class="ma-4">
            Camera access denied. Please enable camera permissions.
          </v-alert>

          <!-- No Camera Found -->
          <v-alert v-if="noCameraFound" type="warning" variant="tonal" class="ma-4">
            No camera detected on this device.
          </v-alert>
        </div>

        <!-- Hidden Canvas -->
        <canvas ref="canvasElement" style="display: none"></canvas>
      </v-card-text>

      <v-card-actions class="pa-4 flex-wrap">
        <v-row>
          <v-col cols="12" sm="6">
            <v-btn
              v-if="!isStreaming && !capturedImage"
              block
              color="primary"
              size="large"
              prepend-icon="mdi-camera"
              @click="startCamera"
              :loading="isLoading"
            >
              Start Camera
            </v-btn>

            <v-btn
              v-if="isStreaming && !capturedImage"
              block
              color="success"
              size="large"
              prepend-icon="mdi-camera-iris"
              @click="capturePhoto"
            >
              Take Photo
            </v-btn>
          </v-col>

          <v-col cols="12" sm="6">
            <v-btn
              v-if="capturedImage"
              block
              color="warning"
              size="large"
              prepend-icon="mdi-camera-retake"
              @click="retakePhoto"
            >
              Retake
            </v-btn>

            <v-btn
              v-if="isStreaming"
              block
              color="error"
              size="large"
              prepend-icon="mdi-camera-off"
              @click="stopCamera"
            >
              Stop Camera
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>

      <!-- Optional: Simple retake/confirm for captured image -->
      <v-expand-transition>
        <div v-if="capturedImage" class="px-4 pb-4">
          <v-divider class="mb-4"></v-divider>
          <v-row>
            <v-col cols="6">
              <v-btn
                block
                color="error"
                variant="tonal"
                prepend-icon="mdi-close"
                @click="retakePhoto"
              >
                Cancel
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn block color="success" prepend-icon="mdi-check" @click="confirmPhoto">
                Use Photo
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

// Props - configuration from parent
const props = defineProps({
  /** Initial camera facing mode: 'environment' (back) or 'user' (front) */
  initialFacingMode: {
    type: String,
    default: "environment",
  },
  /** Image quality (0-1) */
  quality: {
    type: Number,
    default: 0.95,
  },
  /** Auto-start camera on mount */
  autoStart: {
    type: Boolean,
    default: false,
  },
  /** Show confirm/cancel buttons after capture */
  showConfirmButtons: {
    type: Boolean,
    default: true,
  },
});

// Emits - all business logic goes to parent
const emit = defineEmits([
  "camera-started",
  "camera-stopped",
  "camera-switched",
  "photo-captured",
  "photo-confirmed",
  "photo-cancelled",
  "error",
]);

// Refs
const videoElement = ref(null);
const canvasElement = ref(null);
const capturedImage = ref(null);

// UI State
const isLoading = ref(false);
const isStreaming = ref(false);
const permissionDenied = ref(false);
const noCameraFound = ref(false);

// Stream
let mediaStream = null;
let currentFacingMode = props.initialFacingMode;

// Start camera
const startCamera = async () => {
  isLoading.value = true;
  permissionDenied.value = false;
  noCameraFound.value = false;

  try {
    const constraints = {
      video: {
        facingMode: currentFacingMode,
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    };

    mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

    if (videoElement.value) {
      videoElement.value.srcObject = mediaStream;
      isStreaming.value = true;
      emit("camera-started", { facingMode: currentFacingMode });
    }
  } catch (error) {
    console.error("Camera error:", error);

    if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
      permissionDenied.value = true;
    } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
      noCameraFound.value = true;
    }

    emit("error", {
      type: "camera-start",
      error: error.name,
      message: error.message,
    });

    // Try fallback camera
    await tryFallbackCamera();
  } finally {
    isLoading.value = false;
  }
};

// Fallback camera
const tryFallbackCamera = async () => {
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: false,
    });

    if (videoElement.value) {
      videoElement.value.srcObject = mediaStream;
      isStreaming.value = true;
      emit("camera-started", { facingMode: "any" });
    }
  } catch (error) {
    console.error("Fallback camera failed:", error);
    noCameraFound.value = true;
    emit("error", { type: "camera-fallback", error: error.name });
  }
};

// Stop camera
const stopCamera = () => {
  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop());
    mediaStream = null;
  }

  if (videoElement.value) {
    videoElement.value.srcObject = null;
  }

  isStreaming.value = false;
  emit("camera-stopped");
};

// Switch camera
const switchCamera = async () => {
  currentFacingMode = currentFacingMode === "environment" ? "user" : "environment";
  stopCamera();
  await startCamera();
  emit("camera-switched", { facingMode: currentFacingMode });
};

// Capture photo
const capturePhoto = () => {
  if (!isStreaming.value || !videoElement.value || !canvasElement.value) return;

  const video = videoElement.value;
  const canvas = canvasElement.value;
  const context = canvas.getContext("2d");

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  capturedImage.value = canvas.toDataURL("image/jpeg", props.quality);

  emit("photo-captured", {
    image: capturedImage.value,
    width: canvas.width,
    height: canvas.height,
    facingMode: currentFacingMode,
    timestamp: new Date().toISOString(),
  });
};

// Confirm photo (user wants to keep it)
const confirmPhoto = () => {
  emit("photo-confirmed", {
    image: capturedImage.value,
    timestamp: new Date().toISOString(),
  });
};

// Retake photo
const retakePhoto = () => {
  capturedImage.value = null;
  emit("photo-cancelled");
};

// Clear captured image (exposed for parent)
const clearCapture = () => {
  capturedImage.value = null;
};

// Get current image (exposed for parent)
const getCurrentImage = () => {
  return capturedImage.value;
};

// Check camera support
const checkSupport = async () => {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return false;
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter((d) => d.kind === "videoinput");

    if (videoDevices.length === 0) {
      noCameraFound.value = true;
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error checking devices:", error);
    return false;
  }
};

// Lifecycle
onMounted(async () => {
  await checkSupport();
  if (props.autoStart) {
    await startCamera();
  }
});

onUnmounted(() => {
  stopCamera();
});

// Expose methods to parent
defineExpose({
  startCamera,
  stopCamera,
  switchCamera,
  capturePhoto,
  retakePhoto,
  clearCapture,
  getCurrentImage,
});
</script>

<style scoped>
.camera-container {
  max-width: 800px;
  margin: 0 auto;
}

.camera-preview-wrapper {
  background: #000;
  min-height: 400px;
  width: 100%;
  position: relative;
  overflow: hidden;
}

.camera-preview,
.captured-preview {
  width: 100%;
  height: auto;
  min-height: 400px;
  object-fit: cover;
  display: block;
}

.camera-preview.hidden {
  display: none;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10;
}

video {
  transform: scaleX(1);
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
